import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface CartItem {
  cartKey: string;
  id: string;
  name: string;
  price: number;
  image: string;
  quantity: number;
  selectedColor?: string;
}

interface CartContextType {
  items: CartItem[];
  addItem: (item: Omit<CartItem, 'quantity' | 'cartKey'>) => void;
  removeItem: (cartKey: string) => void;
  updateQuantity: (cartKey: string, quantity: number) => void;
  clearCart: () => void;
  totalItems: number;
  totalPrice: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

const buildCartKey = (id: string, selectedColor?: string) => `${id}::${selectedColor || 'NO_COLOR'}`;

const normalizeCartItems = (rawItems: any[]): CartItem[] => {
  const normalizedMap = new Map<string, CartItem>();

  rawItems.forEach((item) => {
    if (!item?.id || !item?.name) return;
    const selectedColor = typeof item.selectedColor === 'string' ? item.selectedColor : undefined;
    const cartKey = item.cartKey || buildCartKey(item.id, selectedColor);
    const quantity = Number.isFinite(Number(item.quantity)) ? Math.max(1, Number(item.quantity)) : 1;

    const existing = normalizedMap.get(cartKey);
    if (existing) {
      existing.quantity += quantity;
      return;
    }

    normalizedMap.set(cartKey, {
      cartKey,
      id: String(item.id),
      name: String(item.name),
      price: Number(item.price) || 0,
      image: String(item.image || ''),
      quantity,
      selectedColor
    });
  });

  return Array.from(normalizedMap.values());
};

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>([]);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      try {
        const parsed = JSON.parse(savedCart);
        setItems(normalizeCartItems(Array.isArray(parsed) ? parsed : []));
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
      }
    }
  }, []);

  // Save cart to localStorage whenever items change
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(items));
  }, [items]);

  const addItem = (item: Omit<CartItem, 'quantity' | 'cartKey'>) => {
    setItems(prevItems => {
      const cartKey = buildCartKey(item.id, item.selectedColor);
      const existingItem = prevItems.find(i => i.cartKey === cartKey);
      if (existingItem) {
        return prevItems.map(i =>
          i.cartKey === cartKey ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [...prevItems, { ...item, cartKey, quantity: 1 }];
    });
  };

  const removeItem = (cartKey: string) => {
    setItems(prevItems => prevItems.filter(item => item.cartKey !== cartKey));
  };

  const updateQuantity = (cartKey: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(cartKey);
      return;
    }
    
    setItems(prevItems =>
      prevItems.map(item =>
        item.cartKey === cartKey ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setItems([]);
  };

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  const value: CartContextType = {
    items,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    totalItems,
    totalPrice
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
